import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Merge, ShieldCheck, Users, RefreshCw } from "lucide-react";

const CONFIDENCE = {
  high: { label: "Very likely", variant: "default" },
  medium: { label: "Likely", variant: "secondary" },
  low: { label: "Possible", variant: "outline" },
};

function DuplicateGroup({ group, onMerged }) {
  const [primary, setPrimary] = useState(group.suggested_primary_id);
  const [busy, setBusy] = useState(false);

  const merge = async () => {
    setBusy(true);
    try {
      await api.post("/contacts/merge", {
        kind: group.kind,
        primary_id: primary,
        duplicate_ids: group.records.map((r) => r.id).filter((id) => id !== primary),
      });
      toast.success("Merged into one record");
      await onMerged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not merge those");
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post("/contacts/duplicates/dismiss", {
        kind: group.kind, ids: group.records.map((r) => r.id),
      });
      toast.success("Kept as separate records");
      await onMerged();
    } catch { toast.error("Could not dismiss that"); }
    finally { setBusy(false); }
  };

  const conf = CONFIDENCE[group.confidence] || CONFIDENCE.low;

  return (
    <Card className="studio-card p-5" data-testid={`duplicate-group-${group.records[0].id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant={conf.variant} data-testid="duplicate-confidence">{conf.label}</Badge>
        <span className="text-sm text-muted-foreground">{group.reason}</span>
        <Badge variant="outline" className="text-[10px]">{group.kind}</Badge>
        <span className="ml-auto text-xs text-muted-foreground">
          {group.records.length} records — keep the one you tick
        </span>
      </div>

      <div className="mt-3 space-y-2">
        {group.records.map((r) => (
          <label key={r.id}
                 data-testid={`duplicate-record-${r.id}`}
                 className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${primary === r.id ? "border-[hsl(var(--studio-primary))] bg-[hsl(var(--studio-primary)/0.04)]" : "border-border hover:bg-accent/40"}`}>
            <input type="radio" className="mt-1" checked={primary === r.id}
                   data-testid={`duplicate-keep-${r.id}`}
                   onChange={() => setPrimary(r.id)} />
            <div className="min-w-0 text-sm">
              <div className="font-medium truncate">{r.name}</div>
              <div className="text-xs text-muted-foreground truncate">
                {[r.role, r.email, r.phone, r.location].filter(Boolean).join(" · ") || "No other details"}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {r.filled_fields} fields filled
                {r.created_at ? ` · added ${new Date(r.created_at).toLocaleDateString()}` : ""}
              </div>
            </div>
          </label>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button size="sm" disabled={busy} data-testid={`duplicate-merge-${group.records[0].id}`} onClick={merge}>
          <Merge className="w-3.5 h-3.5 mr-1.5" /> Merge into one
        </Button>
        <Button size="sm" variant="ghost" disabled={busy}
                data-testid={`duplicate-dismiss-${group.records[0].id}`} onClick={dismiss}>
          <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Not duplicates
        </Button>
        <span className="text-xs text-muted-foreground ml-auto">
          Everything else (projects, invoices, tasks, lists) moves to the record you keep.
        </span>
      </div>
    </Card>
  );
}

export function DuplicatesPanel({ onChanged }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts/duplicates");
      setGroups(data.groups);
    } catch (err) {
      console.error("Could not scan for duplicates", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const afterChange = async () => { await load(); await onChanged(); };

  return (
    <div className="space-y-4" data-testid="duplicates-panel">
      <div className="flex items-center gap-3">
        <p className="text-sm text-muted-foreground">
          Contacts that look like the same person or company — matched on email, phone and name.
        </p>
        <Button variant="outline" size="sm" className="ml-auto" data-testid="duplicates-rescan" onClick={load}>
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-scan
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Scanning your contacts…</div>
      ) : groups.length === 0 ? (
        <Card className="studio-card p-10 text-center" data-testid="duplicates-empty">
          <Users className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-lg font-medium">No duplicates found</div>
          <p className="text-sm text-muted-foreground mt-1">Your contact list is clean.</p>
        </Card>
      ) : (
        groups.map((g) => (
          <DuplicateGroup key={g.records.map((r) => r.id).join("-")} group={g} onMerged={afterChange} />
        ))
      )}
    </div>
  );
}
